import React from "react";
import {
  Factory,
  Trees,
  Cog,
  ShieldCheck,
  Users,
  Boxes,
  Leaf,
} from "lucide-react";
import { BRAND } from "../lib/brand";
import ImageSlider from "../components/ImageSlider";

const SECTIONS = [
  {
    icon: Factory,
    title: "Modern Manufacturing Unit",
    body:
      "Our manufacturing unit is designed with advanced production technology and automated systems that help maintain high production efficiency while ensuring product excellence.",
  },
  {
    icon: Trees,
    title: "High-Quality Raw Materials",
    body:
      "We source premium-quality timber, hardwood, veneers and core materials from trusted suppliers to manufacture strong and durable plywood products.",
  },
  {
    icon: Cog,
    title: "Advanced Machinery",
    body: "Our infrastructure includes:",
    list: [
      "Automatic Hot Press Machines",
      "Cold Press Machines",
      "Precision Cutting Machines",
      "Sanding & Finishing Units",
      "Glue Mixing Systems",
      "Calibration Machines",
      "Quality Testing Equipment",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Quality Control",
    body: "Every product undergoes multiple quality inspection stages to ensure:",
    list: [
      "Strength and durability",
      "Moisture resistance",
      "Termite resistance",
      "Dimensional accuracy",
      "Smooth surface finishing",
    ],
  },
  {
    icon: Users,
    title: "Skilled Workforce",
    body:
      "Our experienced team of engineers, technicians and craftsmen work with precision and dedication to maintain the highest manufacturing standards.",
  },
  {
    icon: Boxes,
    title: "Large Production Capacity",
    body:
      "With a streamlined production process and modern infrastructure, we are capable of handling bulk orders while maintaining timely delivery and consistent quality.",
  },
  {
    icon: Leaf,
    title: "Eco-Friendly Manufacturing",
    body:
      "We follow environmentally responsible manufacturing practices and focus on sustainable production processes to minimise environmental impact.",
  },
];

export default function Infrastructure() {
  return (
    <div data-testid="infrastructure-page">
      <section className="paper-grain">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 py-24 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
                Infrastructure
              </div>
              <h1 className="font-serif text-5xl sm:text-6xl text-wood-800 mt-4 leading-[1.03]">
                Where strength<br />is built.
              </h1>
            </div>
            <div className="lg:col-span-5">
              <p className="text-lg text-wood-700 leading-relaxed">
                At Sylva King Plywood, our state-of-the-art manufacturing
                infrastructure enables us to deliver premium-quality plywood and
                panel products that meet modern industry standards. Our facility
                is equipped with advanced machinery, precision engineering
                systems and strict quality control processes to ensure
                consistency, durability and superior finishing in every product.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-wood-100">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 -mt-2">
          <ImageSlider
            slides={BRAND.infrastructureSlides}
            aspect="aspect-[16/9] sm:aspect-[21/9]"
            testId="infra-slider"
          />
        </div>
      </section>

      <section className="bg-wood-100 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 grid lg:grid-cols-2 gap-6 lg:gap-8">
          {SECTIONS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                data-testid={`infra-section-${i}`}
                className="card-flat rounded-sm bg-white p-8 sm:p-10"
              >
                <Icon className="text-gold-600" size={26} />
                <h2 className="font-serif text-2xl sm:text-3xl text-wood-800 mt-5">
                  {s.title}
                </h2>
                <p className="mt-4 text-wood-700 leading-relaxed">{s.body}</p>
                {s.list && (
                  <ul className="mt-5 grid sm:grid-cols-2 gap-2">
                    {s.list.map((item, j) => (
                      <li
                        key={j}
                        className="flex items-start gap-2 text-sm text-wood-700"
                      >
                        <span className="mt-1 h-1 w-3 bg-gold-500 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
